import { useState } from 'react';

import { Badge, Button, Card, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Select } from '@ds/forms';
import { abrirAnexo } from '@/comuns/abrirAnexo';
import { EnviarAnexoPeloWhatsApp } from '@/comuns/EnviarAnexoPeloWhatsApp';
import { dataEHoraPorExtenso, dataSimples } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { ArquivoDaFicha, CampoDeFicha, FichaPreenchida, ModeloDeFicha, ProntuarioDoPaciente, RespostaDaFicha } from '@/data/tipos';
import { resumoDoBloco } from '@/data/blocosDaFicha';
import { BlocoDeResposta } from '@/screens/fichas/BlocoDeResposta';
import { TextoFormatado } from '@/screens/fichas/TextoFormatado';
import { textoDoRascunho, useRascunhoDaFicha } from './useRascunhoDaFicha';

/** Uma resposta como texto legível, para a leitura do prontuário. */
export function respostaEmTexto(campo: CampoDeFicha, valor: RespostaDaFicha): string {
    if (valor === undefined || valor === '' || (Array.isArray(valor) && valor.length === 0)) return '—';
    if (campo.tipo === 'data') return dataSimples(String(valor));
    /* Bloco composto tem resumo próprio: "IMC 24,2 · Peso adequado" diz o que
       `[object Object]` não diria. */
    const resumo = resumoDoBloco(campo.tipo, valor);
    if (resumo) return resumo;
    if (Array.isArray(valor)) return valor.join(', ');
    return String(valor);
}

export interface PreencherFichaModalProps {
    open: boolean;
    contatoId: string;
    prontuario: ProntuarioDoPaciente;
    /**
     * Consulta aberta com a preferência de rascunho ligada: a ficha se guarda
     * sozinha enquanto se escreve, e volta ao abrir de novo. Ausente, só o
     * botão salva — é o caso do prontuário fora da consulta.
     */
    rascunho?: { atendimentoId: string };
    onClose: () => void;
    onSalva: () => void;
}

export function PreencherFichaModal({ open, contatoId, prontuario, rascunho, onClose, onSalva }: PreencherFichaModalProps) {
    const aviso = useAviso();
    const [modeloId, setModeloId] = useState(prontuario.modelos[0]?.id ?? '');
    const [atendimentoId, setAtendimentoId] = useState(prontuario.atendimentos[0]?.id ?? '');
    const [respostas, setRespostas] = useState<Record<string, RespostaDaFicha>>({});
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const modelo = prontuario.modelos.find((m) => m.id === modeloId);
    const doRascunho = useRascunhoDaFicha(
        rascunho && modeloId ? { atendimentoId: rascunho.atendimentoId, modeloId } : null,
        open,
        /* O que já foi digitado nesta abertura vence o rascunho que chega depois. */
        (recuperadas) => setRespostas((atuais) => (Object.keys(atuais).length > 0 ? atuais : recuperadas)),
    );

    const responder = (uid: string, valor: RespostaDaFicha) => {
        setRespostas((atuais) => {
            const proximas = { ...atuais, [uid]: valor };
            if (rascunho) doRascunho.agendar(proximas);
            return proximas;
        });
        setErro(null);
    };

    const fechar = () => {
        if (rascunho) doRascunho.gravarPendente();
        onClose();
    };

    const salvar = async () => {
        if (!modeloId || !atendimentoId) {
            setErro('Escolha o modelo e a consulta.');
            return;
        }
        setSalvando(true);
        try {
            await dados().salvarFicha({ atendimentoId, contatoId, modeloId, respostas });
            aviso({ title: 'Ficha salva no prontuário', description: modelo?.nome });
            if (rascunho) {
                try {
                    await doRascunho.descartar();
                } catch (e) {
                    aviso({
                        title: 'O rascunho desta ficha não foi apagado',
                        description: e instanceof Error ? e.message : 'Ele continua visível só para você.',
                        tone: 'warning',
                    });
                }
            }
            setRespostas({});
            onSalva();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar a ficha.');
        } finally {
            setSalvando(false);
        }
    };

    const temResposta = Object.keys(respostas).length > 0;

    return (
        <Modal
            open={open} onClose={fechar} width={640} title="Preencher ficha"
            description="A ficha pertence à consulta, não ao paciente: escolha a consulta certa."
            /* Com rascunho, fechar não perde nada — só pede confirmação quando o
               rascunho falhou em guardar. */
            dirty={rascunho ? temResposta && doRascunho.estado.tipo === 'erro' : temResposta}
            onSave={() => void salvar()}
            footer={
                <>
                    <Button variant="secondary" onClick={fechar}>{rascunho ? 'Fechar' : 'Cancelar'}</Button>
                    <Button onClick={() => void salvar()} loading={salvando}>Salvar no prontuário</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {rascunho && (
                    <p
                        aria-live="polite"
                        style={{
                            margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                            color: doRascunho.estado.tipo === 'erro' ? 'var(--danger-strong)' : 'var(--text-secondary)',
                        }}
                    >
                        <Icon name={doRascunho.estado.tipo === 'na-fila' ? 'wifi-off' : 'cloud'} size={13} />
                        {textoDoRascunho(doRascunho.estado)}
                    </p>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <FormField label="Modelo" required>
                        <Select
                            value={modeloId} onChange={(v) => { setModeloId(v); setRespostas({}); }}
                            aria-label="Modelo de ficha"
                            options={prontuario.modelos.map((m) => ({ value: m.id, label: m.nome }))}
                        />
                    </FormField>
                    <FormField label="Consulta" required hint="A ficha entra no histórico desta consulta">
                        <Select
                            value={atendimentoId} onChange={setAtendimentoId}
                            aria-label="Consulta"
                            options={prontuario.atendimentos.map((a) => ({ value: a.id, label: a.rotulo }))}
                        />
                    </FormField>
                </div>

                <Divider spacing={2} />

                {modelo?.campos.map((campo) => (
                    <FormField key={campo.uid} label={campo.rotulo} required={campo.obrigatorio}>
                        <BlocoDeResposta
                            campo={campo}
                            valor={respostas[campo.uid]}
                            onResponder={(v) => responder(campo.uid, v)}
                            onEnviarArquivo={(arquivo) => dados().enviarAnexoDaFicha(contatoId, campo.uid, arquivo)}
                            onAbrirArquivo={(caminho) => dados().enderecoDoAnexo(caminho)}
                        />
                    </FormField>
                ))}
            </div>
        </Modal>
    );
}

/**
 * Os anexos de uma ficha gravada, cada um abrindo no cofre.
 *
 * Contar arquivos ("2 arquivos") e não deixar abrir nenhum é guardar a foto de
 * evolução onde ninguém alcança — o prontuário precisa mostrar o que foi
 * anexado, não só quantos. O endereço é pedido no clique porque é assinado e
 * vence: guardá-lo pronto na tela seria um link que sobrevive a quem o viu.
 */
function AnexosLidos({ arquivos, contatoId }: { arquivos: RespostaDaFicha; contatoId: string }) {
    const [erro, setErro] = useState<string | null>(null);
    const lista = (Array.isArray(arquivos) ? arquivos : []) as ArquivoDaFicha[];
    if (lista.length === 0) return <>—</>;

    const abrir = async (caminho: string) => {
        setErro(null);
        try {
            const aberto = await abrirAnexo(() => dados().enderecoDoAnexo(caminho));
            if (!aberto) setErro('Permita pop-ups para abrir o anexo.');
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível abrir o anexo.');
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
            {lista.map((arquivo) => (
                <span key={arquivo.caminho} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <Button
                        variant="ghost" size="sm"
                        iconLeft={<Icon name={arquivo.tipo.startsWith('image/') ? 'image' : 'file-text'} size={14} />}
                        onClick={() => void abrir(arquivo.caminho)}
                    >
                        {arquivo.nome}
                    </Button>
                    <EnviarAnexoPeloWhatsApp contatoId={contatoId} arquivo={arquivo} />
                </span>
            ))}
            {erro && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{erro}</span>}
        </div>
    );
}

export interface FichaLidaProps {
    ficha: FichaPreenchida;
    /**
     * O modelo de hoje. NÃO é usado para ler a ficha — ver `FichaLida`. Fica no
     * contrato porque as telas o passam; ler por ele é o que não se faz mais.
     */
    modelo?: ModeloDeFicha;
    /** Abre a folha A4, para imprimir ou salvar em PDF. */
    onImprimir?: () => void;
    /**
     * Abre a correção das respostas, com motivo. Ausente quando não há
     * permissão, ou quando a ficha não guardou a cópia do modelo do dia.
     */
    onAlterar?: () => void;
}

/** Uma resposta sem o campo que a explicaria: só o valor, legível o bastante. */
function valorCru(valor: RespostaDaFicha): string {
    if (valor === undefined || valor === null || valor === '' || (Array.isArray(valor) && valor.length === 0)) return '—';
    if (Array.isArray(valor)) {
        return valor
            .map((v) => (typeof v === 'object' && v !== null && 'nome' in v ? String((v as { nome: unknown }).nome) : String(v)))
            .join(', ');
    }
    if (typeof valor === 'object') return JSON.stringify(valor);
    return String(valor);
}

/** Leitura de uma ficha: rótulo e resposta, na ordem do modelo DO DIA. */
export function FichaLida({ ficha, onImprimir, onAlterar }: FichaLidaProps) {
    /* Só a cópia gravada no preenchimento. Prontuário é registro legal: a ficha
       de julho se lê com os rótulos que ela tinha em julho (regra inviolável 4).
       Sem cópia, NÃO se lê pelo modelo de hoje — ele pode ter trocado o rótulo de
       um campo, e a resposta passaria a dizer outra coisa sem ninguém notar.
       Mostra-se o aviso e o que foi gravado, como foi gravado. */
    const campos = ficha.definicaoUsada;
    /* Um minuto de folga: gravar a ficha e o carimbo não acontecem no mesmo instante. */
    const corrigida = new Date(ficha.atualizadoEm).getTime() - new Date(ficha.criadoEm).getTime() > 60_000;
    return (
        <Card
            title={ficha.nome}
            subtitle={`${dataEHoraPorExtenso(ficha.criadoEm)}${ficha.preenchidaPor ? ` · ${ficha.preenchidaPor}` : ''}${corrigida ? ` · corrigida em ${dataEHoraPorExtenso(ficha.atualizadoEm)}` : ''}`}
            padding={18}
            actions={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {ficha.versaoDoModelo !== undefined && <Badge tone="neutral">v{ficha.versaoDoModelo}</Badge>}
                    {campos && <Badge tone="brand">{campos.length} campos</Badge>}
                    {onImprimir && (
                        <Button size="sm" variant="ghost" iconLeft={<Icon name="printer" size={14} />} onClick={onImprimir} aria-label={`Imprimir ${ficha.nome}`}>
                            Imprimir
                        </Button>
                    )}
                    {onAlterar && campos && (
                        <Button size="sm" variant="ghost" iconLeft={<Icon name="pencil" size={14} />} onClick={onAlterar} aria-label={`Corrigir respostas de ${ficha.nome}`}>
                            Corrigir
                        </Button>
                    )}
                </span>
            }
        >
            {!campos ? (
                <div style={{ display: 'grid', gap: 12 }}>
                    <Alert tone="warning" dismissible={false} title="O modelo usado no dia não foi registrado">
                        Esta ficha é anterior à cópia do modelo. As respostas aparecem como foram gravadas, sem os
                        rótulos: lê-las pelo modelo de hoje poderia mudar o sentido delas.
                    </Alert>
                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: '9px 16px', fontSize: 13 }}>
                        {Object.entries(ficha.respostas).map(([chave, valor]) => (
                            <div key={chave} style={{ display: 'contents' }}>
                                <dt style={{ color: 'var(--text-secondary)' }}>{chave}</dt>
                                <dd style={{ margin: 0, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{valorCru(valor)}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            ) : campos.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>
                    O modelo desta ficha não está mais disponível — as respostas seguem guardadas.
                </p>
            ) : (
                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', gap: '9px 16px', fontSize: 13 }}>
                    {campos.map((campo) => (
                        <div key={campo.uid} style={{ display: 'contents' }}>
                            <dt style={{ color: 'var(--text-secondary)' }}>{campo.rotulo}</dt>
                            <dd style={{ margin: 0, color: 'var(--text-primary)', whiteSpace: campo.tipo === 'texto-rico' ? 'normal' : 'pre-wrap' }}>
                                {campo.tipo === 'texto-rico' && ficha.respostas[campo.uid]
                                    /* O texto formatado é lido como foi escrito. Nunca por
                                       HTML: ver a nota em `TextoFormatado`. */
                                    ? <TextoFormatado texto={String(ficha.respostas[campo.uid])} />
                                    : campo.tipo === 'arquivo'
                                        ? <AnexosLidos arquivos={ficha.respostas[campo.uid]} contatoId={ficha.contatoId} />
                                        : respostaEmTexto(campo, ficha.respostas[campo.uid])}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
        </Card>
    );
}
